import React, { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { Heading, Text } from '@/Components/ui/typography';
import { Separator } from '@/Components/ui/separator';
import { Grid, Stack, Inline } from '@/Components/ui/layout';
import { Container } from '@/Components/ui/container';
import { cn } from '@/lib/utils';
import { getColor } from '@/config/theme';
import { CheckCircle2, Clock, Zap, Sparkles, Lock } from 'lucide-react';

export default function TestCardsMinimal({ onSelectTestCard, title = "Select Tests & Interview Flows", description = "Choose tests for your candidate", lockInfo = {} }) {
  const [selectedTestCard, setSelectedTestCard] = useState([]);

  const cards = [
    { id: "/screening", title: "Screening", description: "Assess a candidate's core skills and competencies.", category: "individual" },
    { id: "/communication", title: "Communication", description: "Evaluate a candidate's ability to communicate clearly and professionally.", category: "individual" },
    { id: "/personality-test", title: "Personality", description: "Gain insights into personality traits and cultural fit.", category: "individual" },
    { id: "/behavioral-test", title: "Behavioral", description: "Assess approach to workplace challenges and teamwork.", category: "individual" },
    { id: "/all-test", title: "Personality & Behavioral", description: "Combined personality and behavioral evaluation.", category: "individual" },
    { id: "/icoding", title: "Coding", description: "Assess coding ability with realistic problems.", category: "individual" },
    { id: "/aptitude", title: "Aptitude", description: "Quantitative, logical, verbal and data-interpretation test with MCQ and spoken-answer questions.", category: "individual" },
    { id: "/realtime-screening", title: "Realtime Screening", description: "AI-powered, live proctored screening session with real-time assessments.", category: "realtime" },
    { id: "/realtime-pnb", title: "Realtime P&B", description: "AI-driven, live Personality & Behavioral (PNB) assessment with real-time analysis.", category: "realtime" },
    { id: "/realtime-communication", title: "Realtime Communication", description: "AI-driven, live communication interview with real-time conversational assessment.", category: "realtime" },
    { id: "/quick-screening", title: "Quick Screening", description: "A brief screening to quickly evaluate candidates.", category: "quick" },
    { id: "/quick-pnb", title: "Quick Personality & Behavioral", description: "A short test to assess Personality & Behavioral skills.", category: "quick" },
    { id: "/quick-icoding", title: "Quick Coding", description: "A concise coding test to evaluate programming skills.", category: "quick" },
    { id: "flow:sc", title: "Screening + Coding — 1 hr", description: "30m Screening → 30m Coding", category: "flow", duration: "60m" },
    { id: "flow:spb", title: "Screening + Personality & Behavioral — 1 hr", description: "30m Screening → 30m P&B", category: "flow", duration: "60m" },
    { id: "flow:scp", title: "Screening + Coding + P&B — 1.5 hr", description: "30m Screening → 30m Coding → 30m P&B", category: "flow", duration: "90m" },
    { id: "flow:spc20", title: "Screening + P&B + Coding — 1 hr (20/20/20)", description: "20m Screening → 20m P&B → 20m Coding", category: "flow", duration: "60m (20/20/20)" },
    {
      id: "flow:fscc",
      title: "Screening + Communication + Coding — 1.5 hr",
      description: "30m Screening → 30m Communication → 30m Coding",
      category: "flow",
      duration: "90m"
    }
  ];

  const categorizedCards = useMemo(() => ({
    individual: cards.filter(c => c.category === "individual"),
    realtime: cards.filter(c => c.category === "realtime"),
    quick: cards.filter(c => c.category === "quick"),
    flow: cards.filter(c => c.category === "flow"),
  }), []);

  const toggleCard = (card) => {
    if (lockInfo?.[card.id]?.locked) return;   // can't select a plan-locked test
    const isFlow = card.id.startsWith("flow:");
    setSelectedTestCard((prev) => {
      if (isFlow) {
        return prev.includes(card.id) ? prev.filter(id => id !== card.id) : [card.id];
      } else {
        const withoutFlows = prev.filter(id => !id.startsWith("flow:"));
        return withoutFlows.includes(card.id) 
          ? withoutFlows.filter(id => id !== card.id)
          : [...withoutFlows, card.id];
      }
    });
  };

  const handleConfirm = () => {
    if (onSelectTestCard) onSelectTestCard(selectedTestCard);
  };

  const getCategoryConfig = (category) => {
    const configs = {
      flow: { 
        ring: "ring-purple-500", 
        border: "border-purple-300 dark:border-purple-700",
        bg: "bg-purple-50 dark:bg-purple-950/20", 
        hover: "hover:bg-purple-50/80 dark:hover:bg-purple-950/30 hover:border-purple-400 dark:hover:border-purple-600",
        badge: "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700",
        icon: Sparkles,
        label: "Pre-built Flow"
      },
      quick: { 
        ring: "ring-blue-500", 
        border: "border-blue-300 dark:border-blue-700",
        bg: "bg-blue-50 dark:bg-blue-950/20", 
        hover: "hover:bg-blue-50/80 dark:hover:bg-blue-950/30 hover:border-blue-400 dark:hover:border-blue-600",
        badge: "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700",
        icon: Zap,
        label: "Quick"
      },
      realtime: { 
        ring: "ring-indigo-500", 
        border: "border-indigo-300 dark:border-indigo-700",
        bg: "bg-indigo-50 dark:bg-indigo-950/20", 
        hover: "hover:bg-indigo-50/80 dark:hover:bg-indigo-950/30 hover:border-indigo-400 dark:hover:border-indigo-600",
        badge: "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border-indigo-300 dark:border-indigo-700",
        icon: Clock,
        label: "Realtime"
      },
      individual: { 
        ring: "ring-primary", 
        border: "border-primary/30",
        bg: "bg-primary/10", 
        hover: "hover:bg-primary/5 hover:border-primary/50",
        badge: "bg-secondary/50 text-secondary-foreground border-border/40",
        icon: null,
        label: "Individual"
      },
    };
    return configs[category] || configs.individual;
  };

  const renderCard = (c, compact = false) => {
    const selected = selectedTestCard.includes(c.id);
    const config = getCategoryConfig(c.category);
    const lock = lockInfo?.[c.id];
    const locked = !!lock?.locked;

    return (
      <Card
        key={c.id}
        title={locked ? (lock.reason || "Not included in your plan") : undefined}
        aria-disabled={locked}
        className={cn(
          "group transition-all duration-200",
          locked
            ? "opacity-60 cursor-not-allowed border border-dashed border-destructive/30 bg-destructive/[0.03]"
            : cn(
                "cursor-pointer hover:shadow-md",
                selected
                  ? `ring-2 ${config.ring} ${config.bg} shadow-lg border-2 ${config.border}`
                  : `border ${config.border} ${config.hover}`
              )
        )}
        onClick={() => toggleCard(c)}
        role="button"
        tabIndex={locked ? -1 : 0}
        onKeyDown={(e) => {
          if (locked) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleCard(c);
          }
        }}
      >
        <CardHeader className={cn(compact ? "p-3" : "p-4")}>
          <Inline spacing="sm" align="start" justify="between" className="w-full">
            <div className="flex-1 min-w-0">
              <Inline spacing="xs" align="center" className="mb-1">
                <CardTitle className="text-sm font-semibold leading-tight group-hover:text-foreground transition-colors">
                  {c.title}
                </CardTitle>
                {selected && !locked && (
                  <CheckCircle2 className={cn(
                    "w-4 h-4 shrink-0",
                    config.ring.replace("ring-", "text-")
                  )} />
                )}
                {locked && (
                  <Badge variant="outline" className="text-[10px] font-medium ml-1 gap-1 bg-destructive/10 text-destructive border-destructive/30">
                    <Lock className="w-2.5 h-2.5" />
                    Upgrade
                  </Badge>
                )}
                {c.category === "flow" && c.duration && (
                  <Badge variant="secondary" className="text-[10px] font-medium ml-1">
                    {c.duration}
                  </Badge>
                )}
              </Inline>
              <CardDescription className="text-xs leading-relaxed line-clamp-2 mt-1">
                {locked ? (lock.reason || c.description) : c.description}
              </CardDescription>
            </div>
          </Inline>
        </CardHeader>
      </Card>
    );
  };

  const renderSection = (category, label, colorClass, compact = false) => {
    const sectionCards = categorizedCards[category];
    if (sectionCards.length === 0) return null;

    return (
      <Stack spacing={compact ? "sm" : "md"}>
        <Inline spacing="sm" align="center" className="w-full">
          <Separator className="flex-1" />
          <Badge 
            variant="secondary"
            className={cn(
              "uppercase tracking-wider font-semibold text-xs px-3 py-1",
              colorClass
            )}
          >
            {label}
          </Badge>
          <Separator className="flex-1" />
        </Inline>
        <Grid 
          cols="auto" 
          gap={compact ? "sm" : "md"}
          className={cn(
            compact 
              ? "grid-cols-[repeat(auto-fit,minmax(200px,1fr))]" 
              : "grid-cols-[repeat(auto-fit,minmax(240px,1fr))]"
          )}
        >
          {sectionCards.map(card => renderCard(card, compact))}
        </Grid>
      </Stack>
    );
  };

  return (
    <Container size="default" className="py-6">
      <Stack spacing="lg">
        {/* Header Section */}
        <Inline spacing="md" align="center" justify="between" className="w-full flex-wrap pb-4 border-b">
          <div className="flex-1 min-w-[200px]">
            <Heading level="h3" size="sm" className="mb-1 font-semibold">
              {title}
            </Heading>
            <Text size="sm" variant="muted" className="leading-relaxed">
              {description}
            </Text>
          </div>
          <Inline spacing="xs" align="baseline" className="bg-muted/50 px-4 py-2 rounded-md">
            <Text size="xs" variant="muted" className="uppercase tracking-wide font-semibold">
              Selected:
            </Text>
            <Text className="font-bold text-2xl text-primary leading-none">
              {selectedTestCard.length}
            </Text>
            <Text size="xs" variant="muted" className="font-medium">
              {selectedTestCard.length === 1 ? 'item' : 'items'}
            </Text>
          </Inline>
        </Inline>

        {/* Featured: Pre-built Flows at the top */}
        {categorizedCards.flow.length > 0 && (
          <Stack spacing="md">
            <Inline spacing="sm" align="center" className="w-full">
              <Separator className="flex-1" />
              <Badge 
                variant="secondary"
                className="uppercase tracking-wider font-semibold text-xs px-3 py-1.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700"
              >
                <Sparkles className="w-3 h-3 mr-1.5" />
                Featured: Pre-built Interview Flows
              </Badge>
              <Separator className="flex-1" />
            </Inline>
            <Grid 
              cols="auto" 
              gap="md"
              className="grid-cols-[repeat(auto-fit,minmax(240px,1fr))]"
            >
              {categorizedCards.flow.map(card => renderCard(card, false))}
            </Grid>
          </Stack>
        )}

        {/* Two-column layout for other sections */}
        <Grid cols={1} gap="lg" className="lg:grid-cols-2">
          {/* Left Column */}
          <Stack spacing="lg">
            {renderSection("realtime", "Realtime Tests", "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300", true)}
            {renderSection("quick", "Quick Tests", "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300", true)}
          </Stack>

          {/* Right Column */}
          <Stack spacing="lg">
            {renderSection("individual", "Individual Tests", "bg-muted/80 text-foreground/70", true)}
          </Stack>
        </Grid>

        {/* Footer Actions */}
        <Card>
          <CardContent className="p-4">
            <Inline spacing="md" align="center" justify="between" className="w-full flex-wrap">
              <Text size="sm" variant="muted" className="font-medium">
                {selectedTestCard.length === 0 
                  ? "Select at least one test or flow to continue"
                  : `${selectedTestCard.length} ${selectedTestCard.length === 1 ? 'item' : 'items'} selected`
                }
              </Text>
              <Button
                onClick={handleConfirm}
                disabled={selectedTestCard.length === 0}
                size="lg"
                className="min-w-[160px]"
                style={{
                  backgroundColor: getColor('primary'),
                  color: getColor('primaryForeground'),
                }}
              >
                {selectedTestCard.length > 0 && (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}
                Confirm Selection
              </Button>
            </Inline>
          </CardContent>
        </Card>
      </Stack>
    </Container>
  );
}

